import { useState, useEffect, useRef } from 'react';
import { formatCurrency as formatPrice } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import { Search, Grid, List, Table, Package, DollarSign, Clock, Tag, Plus, X, Save, FolderPlus, HelpCircle, Ban, ChevronUp, ChevronDown, Store, AlertTriangle } from 'lucide-react';
import { apiClient } from '../lib/api';
import { useUser } from '../store/useAuthStore';
import { ProductRequestModal, CreateProductRequest } from '../components/products/ProductRequestModal';
import AddToCatalogModal from '../components/products/AddToCatalogModal';
import { RichTextEditor } from '../components/common/RichTextEditor';
import { StorePricing } from '../components/products/StorePricing';
import { computeVialMath, formatTotalMgPerVial } from '../utils/vialMath';
import { PharmacyBadge } from '../components/pharmacy/PharmacyBadge';
import { PHARMACIES } from '../lib/pharmacyRouting';
import { LifecycleBadge } from '../components/products/LifecycleBadge';
import type { ProductLifecycleStatus } from '@bloom/shared';

interface Product {
  _id: string;
  drugName: string;
  concentration: string;
  format: string;
  size: string;
  displayName: string;
  description?: string;
  categories: string[];
  basePrice: number;
  allowPerUnitOrdering: boolean;
  perUnitPrice?: number;
  images: string[];
  estimatedShipDays: number;
  isActive: boolean;
  lifecycleStatus?: ProductLifecycleStatus;
  productType?: 'medication' | 'supply' | 'lab';
  lab?: {
    panelCode: string;
    cptCode?: string;
    specimenSource?: string;
    requisitionType?: 'at_home_kit' | 'walk_in' | 'mobile_phleb';
    defaultDiagnosisCodes?: string[];
    turnaroundDays?: number;
  };
  isPeptide?: boolean; // true → Blue Five Labs fills it; else Greenstone Rx
  // New fields
  budDays?: string;
  defaultSig?: string;
  sigOptions?: string[];
  productDetails?: string;
  createdAt: string;
  updatedAt: string;
  calculatedPrice?: CalculatedPrice;
}

interface CalculatedPrice {
  productId: string;
  basePrice: number;
  distributorMarkup: number;
  distributorPrice: number;
  physicianMarkup: number;
  finalPrice: number;
  breakdown: {
    distributorMarkupPercentage: number;
    physicianMarkupPercentage: number;
  };
}

interface CategoryMetadata {
  categories: string[];
  formats: string[];
}

interface CreateProductForm {
  drugName: string;
  concentration: string;
  format: string;
  size: string;
  displayName: string;
  description: string;
  categories: string[];
  basePrice: number;
  allowPerUnitOrdering: boolean;
  perUnitPrice: number;
  estimatedShipDays: number;
  productType: 'medication' | 'supply' | 'lab';
  isPeptide: boolean;
  // Lab panel details — only sent when productType is 'lab'.
  labPanelCode: string;
  labCptCode: string;
  labSpecimenSource: string;
  labDiagnosisCodes: string;
  // New fields
  budDays: string;
  defaultSig: string;
  sigOptions: string[];
  productDetails: string;
}

export default function Products() {
  const navigate = useNavigate();
  const user = useUser();
  const isAdmin = user?.role === 'admin';
  const canAddToCatalog = user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'distributor';
  // DTP is on by default — physician counts as DTP unless explicitly opted out (`false`)
  const isDtpPhysician = user?.role === 'physician' && user?.physicianData?.directToPatientEnabled !== false;
  const [activeTab, setActiveTab] = useState<'catalog' | 'store-pricing'>('catalog');
  
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedFormat, setSelectedFormat] = useState('');
  // Admin only: '' lists what is live (active + not making more); otherwise one lifecycle status.
  const [lifecycleFilter, setLifecycleFilter] = useState<'' | 'discontinued' | 'archived'>('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>('table');
  const [metadata, setMetadata] = useState<CategoryMetadata>({ categories: [], formats: [] });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0
  });

  // Product creation modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateProductForm>({
    drugName: '',
    concentration: '',
    format: '',
    size: '',
    displayName: '',
    description: '',
    categories: [],
    basePrice: 0,
    allowPerUnitOrdering: false,
    perUnitPrice: 0,
    estimatedShipDays: 3,
    productType: 'medication',
    isPeptide: false,
    labPanelCode: '',
    labCptCode: '',
    labSpecimenSource: '',
    labDiagnosisCodes: '',
    budDays: '',
    defaultSig: '',
    sigOptions: [],
    productDetails: ''
  });
  const [newCreateSigOption, setNewCreateSigOption] = useState('');
  const [newCreateCategory, setNewCreateCategory] = useState('');
  const [showCreateCategorySuggestions, setShowCreateCategorySuggestions] = useState(false);
  const createCategoryInputRef = useRef<HTMLInputElement>(null);
  const createCategorySuggestionsRef = useRef<HTMLDivElement>(null);

  // Add to catalog modal state
  const [showAddToCatalog, setShowAddToCatalog] = useState(false);
  const [selectedProductForCatalog, setSelectedProductForCatalog] = useState<Product | null>(null);

  // Product request modal state
  const [showRequestModal, setShowRequestModal] = useState(false);

  // Admin-only: products flagged as low-stock / out-of-stock.
  interface StockAlertProduct {
    _id: string;
    displayName: string;
    drugName: string;
    concentration?: string;
    format?: string;
    size?: string;
    lowStockWarning: boolean;
    outOfStock: boolean;
    updatedAt: string;
  }
  const [stockAlerts, setStockAlerts] = useState<{ outOfStock: StockAlertProduct[]; lowStock: StockAlertProduct[] }>({ outOfStock: [], lowStock: [] });

  // Any search or filter change is a new question: go back to page 1. Without
  // this a search typed on page 3 kept asking for page 3 of the new results
  // (empty, while still saying "N found").
  const filterKey = JSON.stringify([searchTerm, selectedCategories, selectedFormat, lifecycleFilter, priceRange]);
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [filterKey]);

  // Only the newest request may write the list (a slow earlier response must
  // not overwrite a newer search).
  const fetchSeq = useRef(0);

  // Debounced search effect
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, selectedCategories, selectedFormat, lifecycleFilter, priceRange, pagination.page]);

  // Initial load
  useEffect(() => {
    fetchMetadata();
    fetchProducts();
    if (isAdmin) fetchStockAlerts();
  }, []);

  const fetchStockAlerts = async () => {
    try {
      const res = await apiClient.get<{ success: boolean; data: { outOfStock: StockAlertProduct[]; lowStock: StockAlertProduct[] } }>('/products/stock-alerts');
      if (res.success && res.data) {
        setStockAlerts({ outOfStock: res.data.outOfStock ?? [], lowStock: res.data.lowStock ?? [] });
      }
    } catch {
      // Non-critical: don't surface a toast for the alert panel.
    }
  };

  const fetchProducts = async () => {
    const mine = ++fetchSeq.current;
    try {
      setLoading(true);
      
      if (user?.role === 'admin' || user?.role === 'distributor' || user?.role === 'supervisor') {
        // Admin/distributor/supervisor use /products endpoint (backend applies catalog filtering)
        const params = new URLSearchParams({
          page: pagination.page.toString(),
          limit: pagination.limit.toString(),
        });

        if (searchTerm) params.append('search', searchTerm);
        if (selectedCategories.length > 0) params.append('categories', selectedCategories.join(','));
        if (selectedFormat) params.append('format', selectedFormat);
        if (isAdmin && lifecycleFilter) params.append('lifecycleStatus', lifecycleFilter);
        if (priceRange.min) params.append('minPrice', priceRange.min);
        if (priceRange.max) params.append('maxPrice', priceRange.max);
        // Distributors/supervisors need pricing calculated from their catalog
        if (!isAdmin) params.append('includePricing', 'true');

        const response = await apiClient.get<{ success: boolean; data: { products: Product[]; pagination: any } }>(`/products?${params}`);
        if (mine !== fetchSeq.current) return;
        if (response.success && response.data) {
          setProducts(response.data.products);
          setPagination({
            page: response.data.pagination.page,
            limit: response.data.pagination.limit,
            total: response.data.pagination.total,
            pages: response.data.pagination.totalPages
          });
          setError('');
        }
      } else {
        // Non-admin users get aggregated products from their assigned catalogs.
        // /products/my-products returns the complete set (no paging), so
        // filtering and paging it here is safe.
        const response = await apiClient.getMyProducts();
        if (mine !== fetchSeq.current) return;
        
        if (response?.success && response.data) {
          let catalogProducts = response.data.products || [];
          
          // Apply client-side filtering for non-admin users
          if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            catalogProducts = catalogProducts.filter((product: any) => 
              product.drugName?.toLowerCase().includes(searchLower) ||
              product.displayName?.toLowerCase().includes(searchLower) ||
              product.categories?.some((cat: string) => cat.toLowerCase().includes(searchLower))
            );
          }
          
          if (selectedCategories.length > 0) {
            catalogProducts = catalogProducts.filter((product: any) =>
              product.categories?.some((cat: string) => selectedCategories.includes(cat))
            );
          }
          
          if (selectedFormat) {
            catalogProducts = catalogProducts.filter((product: any) =>
              product.format?.toLowerCase() === selectedFormat.toLowerCase()
            );
          }
          
          if (priceRange.min || priceRange.max) {
            catalogProducts = catalogProducts.filter((product: any) => {
              const price = product.price || 0;
              const min = priceRange.min ? parseFloat(priceRange.min) : 0;
              const max = priceRange.max ? parseFloat(priceRange.max) : Number.MAX_VALUE;
              return price >= min && price <= max;
            });
          }
          
          // Convert catalog products to the expected Product format
          const formattedProducts = catalogProducts.map((product: any) => {
            // Use the aggregated price from the new system
            const finalPrice = product.price || 0;

            return {
              ...product,
              basePrice: product.basePrice || finalPrice,
              calculatedPrice: {
                productId: product._id,
                basePrice: product.basePrice || finalPrice,
                distributorMarkup: 0,
                distributorPrice: finalPrice,
                physicianMarkup: 0,
                finalPrice: finalPrice,
                breakdown: {
                  distributorMarkupPercentage: 0,
                  physicianMarkupPercentage: 0
                }
              } as CalculatedPrice
            } as Product;
          });
          
          // Client-side pagination. Clamp the page, so a narrower result never
          // shows an empty page past its end.
          const total = formattedProducts.length;
          const pages = Math.ceil(total / pagination.limit);
          const page = Math.min(pagination.page, Math.max(1, pages));
          const start = (page - 1) * pagination.limit;
          const end = start + pagination.limit;
          const paginatedProducts = formattedProducts.slice(start, end);
          
          setProducts(paginatedProducts);
          setPagination({
            ...pagination,
            page,
            total,
            pages
          });
          setError('');
        } else {
          setError(response?.error || 'Failed to fetch catalog products');
          setProducts([]);
          setPagination({ ...pagination, total: 0, pages: 0 });
        }
      }
    } catch (err: any) {
      if (mine !== fetchSeq.current) return;
      setError(err.response?.data?.error || 'Failed to fetch products');
      setProducts([]);
      setPagination({ ...pagination, total: 0, pages: 0 });
    } finally {
      if (mine === fetchSeq.current) setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const response = await apiClient.get<{ data: CategoryMetadata }>('/products/meta/categories');
      setMetadata(response.data);
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
    }
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedFormat('');
    setLifecycleFilter('');
    setPriceRange({ min: '', max: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCreateProduct = async () => {
    try {
      setCreating(true);
      
      const isLab = createForm.productType === 'lab';
      const {
        labPanelCode, labCptCode, labSpecimenSource, labDiagnosisCodes, ...formFields
      } = createForm;

      const productData = {
        ...formFields,
        // A lab panel has no strength, form or fill volume — send nothing
        // rather than empty strings the model would have to interpret.
        concentration: isLab ? undefined : formFields.concentration,
        format: isLab ? undefined : formFields.format,
        size: isLab ? undefined : formFields.size,
        lab: isLab
          ? {
              panelCode: labPanelCode.trim(),
              cptCode: labCptCode.trim() || undefined,
              specimenSource: labSpecimenSource.trim() || undefined,
              requisitionType: 'at_home_kit',
              defaultDiagnosisCodes: labDiagnosisCodes
                .split(',')
                .map(c => c.trim())
                .filter(Boolean)
            }
          : undefined,
        categories: createForm.categories.filter(c => c.trim()),
        basePrice: parseFloat(createForm.basePrice.toString()),
        perUnitPrice: createForm.allowPerUnitOrdering ? parseFloat(createForm.perUnitPrice.toString()) : undefined,
        estimatedShipDays: parseInt(createForm.estimatedShipDays.toString()),
        budDays: createForm.budDays || undefined,
        defaultSig: createForm.sigOptions.length > 0 ? createForm.sigOptions[0] : (createForm.defaultSig || undefined),
        sigOptions: createForm.sigOptions.length > 0 ? createForm.sigOptions : undefined,
        productDetails: createForm.productDetails || undefined
      };

      const response = await apiClient.post<{ data: Product }>('/products', productData);
      
      // Add the new product to the list
      setProducts(prev => [response.data, ...prev]);
      
      // Reset form and close modal
      setCreateForm({
        drugName: '',
        concentration: '',
        format: '',
        size: '',
        displayName: '',
        description: '',
        categories: [],
        basePrice: 0,
        allowPerUnitOrdering: false,
        perUnitPrice: 0,
        estimatedShipDays: 3,
        productType: 'medication',
        isPeptide: false,
        labPanelCode: '',
        labCptCode: '',
        labSpecimenSource: '',
        labDiagnosisCodes: '',
        budDays: '',
        defaultSig: '',
        sigOptions: [],
        productDetails: ''
      });
      setNewCreateSigOption('');
      setShowCreateModal(false);
      
      // Refresh products list to get updated pagination
      fetchProducts();
      
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create product');
    } finally {
      setCreating(false);
    }
  };

  const handleFormChange = (field: keyof CreateProductForm, value: any) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addCategory = (category: string) => {
    if (category.trim() && !createForm.categories.includes(category.trim())) {
      setCreateForm(prev => ({
        ...prev,
        categories: [...prev.categories, category.trim()]
      }));
    }
  };

  const removeCategory = (category: string) => {
    setCreateForm(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const moveCreateSigOption = (index: number, direction: 'up' | 'down') => {
    const options = [...createForm.sigOptions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= options.length) return;
    [options[index], options[newIndex]] = [options[newIndex], options[index]];
    handleFormChange('sigOptions', options);
  };

  const filteredCreateCategories = metadata.categories.filter(
    c => c.toLowerCase().includes(newCreateCategory.toLowerCase()) && !createForm.categories.includes(c)
  );

  // Close category suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        createCategorySuggestionsRef.current && !createCategorySuggestionsRef.current.contains(e.target as Node) &&
        createCategoryInputRef.current && !createCategoryInputRef.current.contains(e.target as Node)
      ) {
        setShowCreateCategorySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProductRequestSuccess = (request: CreateProductRequest) => {
    // The modal handles the backend submission and navigation
    console.log('Product request submitted:', request);
  };


  const getProductImage = (product: Product) => {
    if (product.images && product.images.length > 0) {
      return product.images[0];
    }
    
    // Generate a placeholder based on drug category/type
    const colors = [
      'bg-gradient-to-br from-blue-400 to-blue-600',
      'bg-gradient-to-br from-green-400 to-green-600',
      'bg-gradient-to-br from-purple-400 to-purple-600',
      'bg-gradient-to-br from-pink-400 to-pink-600',
      'bg-gradient-to-br from-indigo-400 to-indigo-600',
    ];
    const colorIndex = product.drugName.charCodeAt(0) % colors.length;
    return colors[colorIndex];
  };

  // Helper function to show price with appropriate context
  const renderProductPrice = (product: Product) => {
    const perUnitPrice = getPerUnitPrice(product);

    if (isAdmin) {
      return (
        <div>
          <div className="flex items-center">
            <DollarSign className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-lg font-bold text-gray-900">{formatPrice(product.basePrice)}</span>
          </div>
          <div className="text-xs text-blue-600 font-medium">Base Price</div>
          {perUnitPrice && (
            <div className="text-sm text-gray-600 mt-1">
              {formatPrice(perUnitPrice)} per unit
            </div>
          )}
        </div>
      );
    } else {
      // For distributors/supervisors/physicians, show catalog price
      if (product.calculatedPrice) {
        const displayPrice = product.calculatedPrice.finalPrice;
        const priceLabel = "Your Cost";

        return (
          <div>
            <div className="flex items-center">
              <DollarSign className="w-4 h-4 text-green-600 mr-1" />
              <span className="text-lg font-bold text-gray-900">{formatPrice(displayPrice)}</span>
            </div>
            <div className="text-xs text-green-600 font-medium">{priceLabel}</div>
            {perUnitPrice && (
              <div className="text-sm text-gray-600 mt-1">
                {formatPrice(perUnitPrice)} per unit
              </div>
            )}
          </div>
        );
      } else {
        // No pricing available - show error
        return (
          <div>
            <div className="flex items-center">
              <X className="w-4 h-4 text-red-500 mr-1" />
              <span className="text-sm text-red-600">Pricing unavailable</span>
            </div>
          </div>
        );
      }
    }
  };

  // Helper function for table view pricing
  const renderTablePrice = (product: Product) => {
    if (isAdmin) {
      return (
        <div>
          <span className="font-medium">{formatPrice(product.basePrice)}</span>
          <div className="text-xs text-gray-500">Base Price</div>
        </div>
      );
    } else {
      if (product.calculatedPrice) {
        const displayPrice = product.calculatedPrice.finalPrice;
        const priceLabel = "Your Cost";

        return (
          <div>
            <span className="font-medium">{formatPrice(displayPrice)}</span>
            <div className="text-xs text-green-600">{priceLabel}</div>
          </div>
        );
      } else {
        return (
          <div>
            <span className="text-red-600 text-sm">Unavailable</span>
            <div className="text-xs text-red-500">No pricing</div>
          </div>
        );
      }
    }
  };

  // Per-unit price. Admins get the stored (base-tier) value; for everyone else
  // the backend restates it at the caller's own price, or omits it when the
  // caller has no price for the product.
  const getPerUnitPrice = (product: Product) => {
    if (!product.allowPerUnitOrdering || !product.perUnitPrice) {
      return null;
    }
    return product.perUnitPrice;
  };

  const renderVialTotal = (product: Product) => {
    const vm = computeVialMath(product);
    const label = formatTotalMgPerVial(vm);
    if (!label) return null;
    return (
      <div className="flex items-center gap-1 text-xs text-gray-500">
        <span><span className="font-medium text-gray-700">{label}</span> <span className="text-gray-400">(concentration × size)</span></span>
        {isAdmin && vm.perMlMissing && (
          <span title='Concentration is missing "/mL" — please update so it reads e.g. "10 mg/mL".' className="inline-flex">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          </span>
        )}
      </div>
    );
  };

  const ProductCard = ({ product }: { product: Product }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 group overflow-hidden">
      {/* Product Image/Placeholder */}
      <div className={`h-48 ${getProductImage(product)} flex items-center justify-center relative`}>
        <Package className="w-12 h-12 text-white/80" />
        <div className="absolute top-3 right-3">
          <div className="bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 text-xs font-medium text-gray-700">
            {product.format}
          </div>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-5">
        <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
          {product.displayName}
          <span className="ml-2 align-middle"><LifecycleBadge status={product.lifecycleStatus} audience={isAdmin ? 'admin' : 'clinic'} /></span>
        </h3>
        <div className="mb-2">{renderVialTotal(product)}</div>

        {product.description && (
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>
        )}

        {/* Categories */}
        <div className="flex flex-wrap gap-1 mb-3">
          {product.categories.slice(0, 3).map(category => (
            <span
              key={category}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
            >
              <Tag className="w-3 h-3 mr-1" />
              {category}
            </span>
          ))}
          {product.categories.length > 3 && (
            <span className="text-xs text-gray-500">+{product.categories.length - 3}</span>
          )}
        </div>

        {/* Pricing */}
        <div className="flex items-center justify-between mb-3">
          {renderProductPrice(product)}
          <div className="text-right">
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="w-4 h-4 mr-1" />
              {product.estimatedShipDays} days
            </div>
          </div>
        </div>

        {/* Ordering Info */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {product.allowPerUnitOrdering ? 'Per-unit ordering' : 'Item ordering'}
          </div>
          <button 
            onClick={() => navigate(`/products/${product._id}`)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );

  const ProductListItem = ({ product }: { product: Product }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 p-6">
      <div className="flex items-start space-x-4">
        <div className={`w-20 h-20 ${getProductImage(product)} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Package className="w-8 h-8 text-white/80" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {product.displayName}
                <span className="ml-2 align-middle"><LifecycleBadge status={product.lifecycleStatus} audience={isAdmin ? 'admin' : 'clinic'} /></span>
              </h3>
              <div className="mt-1">{renderVialTotal(product)}</div>
              {product.description && (
                <p className="text-gray-600 mt-1">{product.description}</p>
              )}
            </div>
            <div className="flex items-center space-x-4 flex-shrink-0">
              <div className="text-right">
                {isAdmin ? (
                  <>
                    <div className="text-xl font-bold text-gray-900">{formatPrice(product.basePrice)}</div>
                    <div className="text-xs text-blue-600 font-medium">Base Price</div>
                  </>
                ) : product.calculatedPrice ? (
                  <>
                    <div className="text-xl font-bold text-gray-900">{formatPrice(product.calculatedPrice.finalPrice)}</div>
                    <div className="text-xs text-green-600 font-medium">Your Cost</div>
                  </>
                ) : (
                  <span className="text-sm text-red-600">Pricing unavailable</span>
                )}
                {product.allowPerUnitOrdering && product.perUnitPrice && (
                  <div className="text-sm text-gray-600">{formatPrice(product.perUnitPrice)} per unit</div>
                )}
              </div>
              <button 
                onClick={() => navigate(`/products/${product._id}`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                View Details
              </button>
            </div>
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <div className="flex flex-wrap gap-2">
              {product.categories.slice(0, 4).map(category => (
                <span
                  key={category}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                >
                  {category}
                </span>
              ))}
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {product.estimatedShipDays} days
              </span>
              <span>{product.format}</span>
              <span>{product.allowPerUnitOrdering ? 'Per-unit' : 'Item'} ordering</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleAddToCatalog = (product: Product) => {
    setSelectedProductForCatalog(product);
    setShowAddToCatalog(true);
  };

  const handleDisableProduct = async (product: Product) => {
    const confirmed = window.confirm(
      `Are you sure you want to disable "${product.displayName}"?\n\nThis will remove it from the product list. The product cannot be disabled if it's currently in any catalogs.`
    );
    if (!confirmed) return;

    try {
      const response = await apiClient.disableProduct(product._id);
      if (response.success) {
        // Remove from local state
        setProducts(prev => prev.filter(p => p._id !== product._id));
        alert('Product disabled successfully');
      } else {
        alert(response.error || 'Failed to disable product');
      }
    } catch (error) {
      console.error('Error disabling product:', error);
      alert('Failed to disable product');
    }
  };

  const ProductTable = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Mobile Card View */}
      <div className="block lg:hidden">
        <div className="divide-y divide-gray-200">
          {products.map((product) => (
            <div key={product._id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {product.displayName}
                    <span className="ml-2 align-middle"><LifecycleBadge status={product.lifecycleStatus} audience={isAdmin ? 'admin' : 'clinic'} /></span>
                  </div>
                  <div className="mb-1">{renderVialTotal(product)}</div>
                  {product.description && (
                    <div className="text-xs text-gray-500 mb-2 line-clamp-2">
                      {product.description}
                    </div>
                  )}
                  
                  {/* Mobile metadata */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {product.format}
                    </span>
                    {product.categories.slice(0, 1).map(category => (
                      <span
                        key={category}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                      >
                        {category}
                      </span>
                    ))}
                    {product.categories.length > 1 && (
                      <span className="text-xs text-gray-500">+{product.categories.length - 1}</span>
                    )}
                    {/* A lab panel is filled by the lab vendor, so the
                        peptide-derived pharmacy badge does not apply to it. */}
                    {product.productType === 'lab' ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                        Lab
                      </span>
                    ) : (
                      <PharmacyBadge
                        pharmacy={product.isPeptide ? PHARMACIES.bluefive : PHARMACIES.greenstone}
                        size="sm"
                      />
                    )}
                  </div>

                  {/* Mobile details row */}
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <div className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {product.estimatedShipDays} days
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      product.allowPerUnitOrdering 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {product.allowPerUnitOrdering ? 'Per-unit' : 'Item'}
                    </span>
                  </div>
                </div>

                {/* Mobile price and action */}
                <div className="ml-4 flex flex-col items-end gap-2">
                  <div className="text-right">
                    {renderTablePrice(product)}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {canAddToCatalog && (
                      <button
                        onClick={() => handleAddToCatalog(product)}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs transition-colors flex items-center gap-1"
                      >
                        <FolderPlus className="w-3 h-3" />
                        Add to Catalog
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/products/${product._id}`)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-xs transition-colors"
                    >
                      View
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDisableProduct(product)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-xs transition-colors flex items-center gap-1"
                      >
                        <Ban className="w-3 h-3" />
                        Disable
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block data-table-container">
        <div className="data-table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Format</th>
                <th>Categories</th>
                <th>Price</th>
                <th>Ship Time</th>
                <th>Ordering</th>
                <th>Fulfilled by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product._id}>
                  <td className="cell-primary">
                    <div className="cell-stack">
                      <span className="cell-stack-value">
                        {product.displayName}
                        <span className="ml-2 align-middle"><LifecycleBadge status={product.lifecycleStatus} audience={isAdmin ? 'admin' : 'clinic'} /></span>
                        {product.productType === 'supply' && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Supply</span>
                        )}
                      </span>
                      {renderVialTotal(product)}
                      {product.description && (
                        <span className="cell-stack-label cell-truncate max-w-xs">{product.description}</span>
                      )}
                    </div>
                  </td>
                  <td className="cell-secondary">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {product.format}
                    </span>
                  </td>
                  <td className="cell-secondary">
                    <div className="flex flex-wrap gap-1">
                      {product.categories.slice(0, 2).map(category => (
                        <span
                          key={category}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                        >
                          {category}
                        </span>
                      ))}
                      {product.categories.length > 2 && (
                        <span className="text-xs text-gray-500">+{product.categories.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="cell-mono">
                    {renderTablePrice(product)}
                  </td>
                  <td className="cell-secondary">
                    <div className="cell-inline cell-inline-icon">
                      <Clock className="w-4 h-4" />
                      {product.estimatedShipDays} days
                    </div>
                  </td>
                  <td className="cell-secondary">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      product.allowPerUnitOrdering
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {product.allowPerUnitOrdering ? 'Per-unit' : 'Item'}
                    </span>
                  </td>
                  <td className="cell-secondary">
                    {/* A lab panel is filled by the lab vendor, so the
                        peptide-derived pharmacy badge does not apply to it. */}
                    {product.productType === 'lab' ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                        Lab
                      </span>
                    ) : (
                      <PharmacyBadge
                        pharmacy={product.isPeptide ? PHARMACIES.bluefive : PHARMACIES.greenstone}
                        size="sm"
                      />
                    )}
                  </td>
                  <td className="cell-actions">
                    <div className="table-actions">
                      {canAddToCatalog && (
                        <button
                          onClick={() => handleAddToCatalog(product)}
                          className="table-action-btn table-action-btn-success"
                          title="Add to Catalog"
                        >
                          <FolderPlus className="w-4 h-4" />
                          Add
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/products/${product._id}`)}
                        className="table-action-btn table-action-btn-primary"
                      >
                        View Details
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDisableProduct(product)}
                          className="table-action-btn table-action-btn-danger"
                          title="Disable Product"
                        >
                          <Ban className="w-4 h-4" />
                          Disable
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600">Browse and manage your product catalog</p>
        </div>

        {/* Create product button for admins */}
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </button>
        )}
      </div>

      {/* Admin stock-status panel: products currently flagged out-of-stock
          or low-stock. Hidden when nothing is flagged. */}
      {isAdmin && (stockAlerts.outOfStock.length + stockAlerts.lowStock.length) > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-amber-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <h2 className="text-sm font-medium text-amber-900">
              Stock alerts ({stockAlerts.outOfStock.length} out of stock · {stockAlerts.lowStock.length} low stock)
            </h2>
          </div>
          <table className="min-w-full divide-y divide-amber-200 text-sm">
            <thead className="bg-amber-100/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Product</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Last updated</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100">
              {[...stockAlerts.outOfStock, ...stockAlerts.lowStock].map(p => (
                <tr key={p._id} className="hover:bg-amber-100/40">
                  <td className="px-4 py-2 text-gray-900">
                    <div className="font-medium">{p.displayName}</div>
                    {(p.concentration || p.format || p.size) && (
                      <div className="text-xs text-gray-500">
                        {[p.drugName, p.concentration, p.format, p.size].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {p.outOfStock ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        Out of stock
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        Low stock
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {new Date(p.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => navigate(`/products/${p._id}`)}
                      className="text-xs text-amber-900 hover:text-amber-700 font-medium hover:underline"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab bar for DTP physicians */}
      {isDtpPhysician && (
        <div className="border-b border-gray-200">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('catalog')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'catalog'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Catalog
            </button>
            <button
              onClick={() => setActiveTab('store-pricing')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === 'store-pricing'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Store className="w-4 h-4" />
              Store Pricing
            </button>
          </div>
        </div>
      )}

      {/* Store Pricing tab content */}
      {isDtpPhysician && activeTab === 'store-pricing' ? (
        <StorePricing />
      ) : (
      <>
      {/* Filters Section */}
      <div className="bg-gray-50 rounded-lg p-4 lg:p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters & Search</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Products</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Format Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
            <select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            >
              <option value="">All Formats</option>
              {metadata?.formats?.map(format => (
                <option key={format} value={format}>{format}</option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lifecycle</label>
              <select
                value={lifecycleFilter}
                onChange={(e) => setLifecycleFilter(e.target.value as '' | 'discontinued' | 'archived')}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
              >
                <option value="">Live products</option>
                <option value="discontinued">Not making more</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )}

          {/* Price Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
            <div className="grid grid-cols-2 gap-1">
              <input
                type="number"
                placeholder="Min"
                value={priceRange.min}
                onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                className="px-2 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <input
                type="number"
                placeholder="Max"
                value={priceRange.max}
                onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                className="px-2 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2.5 text-gray-600 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg font-medium transition-colors text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Categories */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Categories</label>
          <div className="flex flex-wrap gap-2">
            {metadata?.categories?.map(category => (
              <label key={category} className="flex items-center bg-white px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category)}
                  onChange={() => handleCategoryToggle(category)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                />
                <span className="text-sm text-gray-700 capitalize">{category}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-4 lg:space-y-6">
        {/* Results and View Controls */}
        <div className="flex items-center justify-between gap-4">
          {/* Results count */}
          <div className="text-sm text-gray-600">
            {!loading && (
              <span>
                {pagination.total} product{pagination.total !== 1 ? 's' : ''} 
                {(searchTerm || selectedCategories.length > 0 || selectedFormat || priceRange.min || priceRange.max) && ' found'}
              </span>
            )}
          </div>

          {/* View mode switcher - hidden on mobile */}
          <div className="hidden lg:flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Table View"
            >
              <Table className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Grid View"
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Products display */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                  <div className="w-20 h-8 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 mb-2">Error loading products</div>
            <div className="text-gray-600">{error}</div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="products-container">
            {products.map(product => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-4">
            {products.map(product => (
              <ProductListItem key={product._id} product={product} />
            ))}
          </div>
        ) : (
          <div data-testid="products-container">
            <ProductTable />
          </div>
        )}

        {/* Pagination - shown for all view modes */}
        {!loading && pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {/* Show page numbers */}
                {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                  const pageNum = Math.max(1, pagination.page - 2) + i;
                  if (pageNum > pagination.pages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                      className={`px-3 py-2 rounded-lg text-sm ${
                        pageNum === pagination.page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Product Request Section - Only for distributors and physicians */}
      {!isAdmin && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 lg:p-8 border border-blue-100">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Can't find what you need?
                </h3>
                <p className="text-gray-600 text-sm">
                  Create a product request and we'll do our best to add it to our catalog.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowRequestModal(true)}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm hover:shadow-md whitespace-nowrap"
            >
              <Package className="w-5 h-5 mr-2" />
              Create a product request
            </button>
          </div>
        </div>
      )}

      {/* Create Product Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Add New Product</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Product Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
                <select
                  value={createForm.productType}
                  onChange={(e) => handleFormChange('productType', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="medication">Medication</option>
                  <option value="supply">Supply (e.g., syringes, needles, alcohol swabs)</option>
                  <option value="lab">Lab test (at-home collection kit)</option>
                </select>
                {createForm.productType === 'supply' && (
                  <p className="text-xs text-amber-600 mt-1">
                    Supply products use flat base price (no catalog markup chain) and are not sent to pharmacy.
                  </p>
                )}
                {createForm.productType === 'lab' && (
                  <p className="text-xs text-amber-600 mt-1">
                    Lab tests are fulfilled by the lab vendor, not the pharmacy — the kit is mailed
                    to the patient and results post back automatically. Base price is the vendor
                    cost; the normal catalog markup chain applies on top. An ordering provider NPI
                    is always required, so a lab test can never be prescriber-optional.
                  </p>
                )}
              </div>

              {/* Peptide flag — drives which pharmacy fills the order.
                  Peptides → Blue Five Labs; FDA drugs/compounds → Greenstone Rx. */}
              {createForm.productType !== 'supply' && createForm.productType !== 'lab' && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createForm.isPeptide}
                    onChange={(e) => handleFormChange('isPeptide', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Peptide
                    <span className="block text-xs text-gray-400">
                      Filled by Blue Five Labs (all 50 states). Leave off for FDA drugs/compounds (Greenstone Rx).
                    </span>
                  </span>
                </label>
              )}

              {/* Basic Product Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {createForm.productType === 'lab' ? 'Panel Name' : 'Drug Name'}
                  </label>
                  <input
                    type="text"
                    value={createForm.drugName}
                    onChange={(e) => handleFormChange('drugName', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={createForm.productType === 'lab' ? 'e.g., Male Hormone Panel' : 'e.g., Semaglutide'}
                  />
                </div>

                {/* A lab panel is identified by its panel code, not by a
                    strength/form/size triple — those fields are replaced here
                    rather than left blank. */}
                {createForm.productType === 'lab' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Panel Code</label>
                      <input
                        type="text"
                        value={createForm.labPanelCode}
                        onChange={(e) => handleFormChange('labPanelCode', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., MALE_HORMONE"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Must match a panel code in the lab vendor catalog — this is how orders are
                        routed and results are matched back.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CPT Code</label>
                      <input
                        type="text"
                        value={createForm.labCptCode}
                        onChange={(e) => handleFormChange('labCptCode', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Specimen Source</label>
                      <input
                        type="text"
                        value={createForm.labSpecimenSource}
                        onChange={(e) => handleFormChange('labSpecimenSource', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Serum, Dried blood spot"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Diagnosis Codes
                      </label>
                      <input
                        type="text"
                        value={createForm.labDiagnosisCodes}
                        onChange={(e) => handleFormChange('labDiagnosisCodes', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Comma-separated ICD-10, e.g., E03.9, R53.83"
                      />
                    </div>
                  </>
                ) : (
                <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Concentration</label>
                  <input
                    type="text"
                    value={createForm.concentration}
                    onChange={(e) => handleFormChange('concentration', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 2.5mg/ml"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                  <select
                    value={createForm.format}
                    onChange={(e) => handleFormChange('format', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Format</option>
                    {metadata.formats.map(format => (
                      <option key={format} value={format}>{format}</option>
                    ))}
                    <option value="Injectable">Injectable</option>
                    <option value="ODT">ODT</option>
                    <option value="Tablet">Tablet</option>
                    <option value="Capsule">Capsule</option>
                    <option value="Cream">Cream</option>
                    <option value="Gel">Gel</option>
                    <option value="Lozenge">Lozenge</option>
                    <option value="Nasal Spray">Nasal Spray</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                  <input
                    type="text"
                    value={createForm.size}
                    onChange={(e) => handleFormChange('size', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 1ml vial, 30ct"
                  />
                </div>
                </>
                )}
              </div>

              {/* Display Name & Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input
                  type="text"
                  value={createForm.displayName}
                  onChange={(e) => handleFormChange('displayName', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Leave empty to auto-generate"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {createForm.productType === 'lab'
                    ? `If left empty, the panel name is used as-is: "${createForm.drugName}"`
                    : `If left empty, will be generated as "${createForm.drugName} ${createForm.concentration} ${createForm.format} ${createForm.size}"`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional product description"
                />
              </div>

              {/* Categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {createForm.categories.map(category => (
                    <span
                      key={category}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {category}
                      <button
                        onClick={() => removeCategory(category)}
                        className="hover:text-blue-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="relative">
                  <div className="flex gap-2">
                    <input
                      ref={createCategoryInputRef}
                      type="text"
                      value={newCreateCategory}
                      onChange={(e) => {
                        setNewCreateCategory(e.target.value);
                        setShowCreateCategorySuggestions(true);
                      }}
                      onFocus={() => setShowCreateCategorySuggestions(true)}
                      placeholder="Search or add category"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (newCreateCategory.trim()) {
                            addCategory(newCreateCategory.trim());
                            setNewCreateCategory('');
                            setShowCreateCategorySuggestions(false);
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newCreateCategory.trim()) {
                          addCategory(newCreateCategory.trim());
                          setNewCreateCategory('');
                          setShowCreateCategorySuggestions(false);
                        }
                      }}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Add
                    </button>
                  </div>
                  {showCreateCategorySuggestions && filteredCreateCategories.length > 0 && (
                    <div
                      ref={createCategorySuggestionsRef}
                      className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                    >
                      {filteredCreateCategories.map(cat => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            addCategory(cat);
                            setNewCreateCategory('');
                            setShowCreateCategorySuggestions(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-700"
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* BUD Days and Default Sig */}
              {createForm.productType !== 'lab' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BUD (Beyond Use Date)</label>
                  <select
                    value={createForm.budDays}
                    onChange={(e) => handleFormChange('budDays', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select BUD</option>
                    <option value="30 days">30 days</option>
                    <option value="60 days">60 days</option>
                    <option value="90 days">90 days</option>
                    <option value="180 days">180 days</option>
                    <option value="1 year">1 year</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Shelf life for this product variant
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sig Options</label>
                  <div className="space-y-2">
                    {createForm.sigOptions.map((sig, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => moveCreateSigOption(index, 'up')}
                            disabled={index === 0}
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-0.5"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveCreateSigOption(index, 'down')}
                            disabled={index === createForm.sigOptions.length - 1}
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-0.5"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <span className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                          {index === 0 && <span className="text-xs text-blue-600 font-medium mr-2">(default)</span>}
                          {sig}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = createForm.sigOptions.filter((_, i) => i !== index);
                            handleFormChange('sigOptions', updated);
                          }}
                          className="text-gray-400 hover:text-red-500 p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCreateSigOption}
                        onChange={(e) => setNewCreateSigOption(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newCreateSigOption.trim()) {
                            e.preventDefault();
                            handleFormChange('sigOptions', [...createForm.sigOptions, newCreateSigOption.trim()]);
                            setNewCreateSigOption('');
                          }
                        }}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Add sig option, e.g., Take as directed"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (newCreateSigOption.trim()) {
                            handleFormChange('sigOptions', [...createForm.sigOptions, newCreateSigOption.trim()]);
                            setNewCreateSigOption('');
                          }
                        }}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    First option is the default. Physicians can also write custom instructions.
                  </p>
                </div>
              </div>
              )}

              {/* Product Details (Rich Text) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Details</label>
                <RichTextEditor
                  content={createForm.productDetails}
                  onChange={(html) => handleFormChange('productDetails', html)}
                  placeholder="Add detailed product information..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Additional product information (dosing, storage, etc.)
                </p>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Price ($)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-sm text-gray-600">$</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={createForm.basePrice}
                      onChange={(e) => handleFormChange('basePrice', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Ship Days</label>
                  <input
                    type="number"
                    min="1"
                    value={createForm.estimatedShipDays}
                    onChange={(e) => handleFormChange('estimatedShipDays', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Per-Unit Ordering */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="allowPerUnit"
                  checked={createForm.allowPerUnitOrdering}
                  onChange={(e) => handleFormChange('allowPerUnitOrdering', e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <label htmlFor="allowPerUnit" className="text-sm font-medium text-gray-700">
                    Allow Per-Unit Ordering
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable physicians to order individual units rather than full packages
                  </p>
                  {createForm.allowPerUnitOrdering && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Per-Unit Price ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={createForm.perUnitPrice}
                        onChange={(e) => handleFormChange('perUnitPrice', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProduct}
                disabled={
                  creating ||
                  !createForm.drugName ||
                  // A lab panel is identified by its panel code; format and size
                  // are not fields it has.
                  (createForm.productType === 'lab'
                    ? !createForm.labPanelCode
                    : !createForm.format || !createForm.size)
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {creating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Create Product
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Catalog Modal */}
      {selectedProductForCatalog && (
        <AddToCatalogModal
          isOpen={showAddToCatalog}
          onClose={() => {
            setShowAddToCatalog(false);
            setSelectedProductForCatalog(null);
          }}
          product={selectedProductForCatalog}
          onSuccess={() => {
            // Optionally refresh products or do nothing
          }}
        />
      )}

      {/* Product Request Modal */}
      <ProductRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSuccess={handleProductRequestSuccess}
      />
      </>
      )}
    </div>
  );
} 